import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import SideBar from "./SideBar";
import axios from "axios";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faUsers,
  faNewspaper,
  faHeart,
  faCamera,
  faCalendarAlt,
  faMapMarkerAlt,
  faCheckCircle,
  faStar,
  faChartLine,
  faArrowRight,
  faArrowLeft,
  faTrophy,
} from "@fortawesome/free-solid-svg-icons";


const API_URL = process.env.REACT_APP_API_URL_INFLUENCER;

const InfluencerProfile = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // ── fetch profile ────────────────────────────────────────────────
  const fetchProfile = useCallback(
    async (username) => {
      if (!username || !username.trim()) return;
      setLoading(true);
      setError("");
      setProfile(null);

      try {
        const res = await axios.get(
          `${API_URL}/influencer?username=${encodeURIComponent(
            username.trim()
          )}`
        );

        if (res.data?.status === "success" && res.data?.data) {
          setProfile(res.data.data);
        } else {
          setError("Influencer not found.");
        }
      } catch (err) {
        console.error("Error fetching influencer profile:", err);
        setError(
          err.response?.data?.message ||
            err.response?.data?.error ||
            "Failed to fetch influencer profile."
        );
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // ── auto-fetch from URL param ────────────────────────────────────
  useEffect(() => {
    const usernameParam = searchParams.get("username");
    if (usernameParam) {
      fetchProfile(usernameParam);
    } else {
      setError("No username provided. Go back to the leaderboard to select an influencer.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // ── helpers ──────────────────────────────────────────────────────
  const formatNumber = (num) => {
    if (!num && num !== 0) return "—";
    if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + "M";
    if (num >= 1_000) return (num / 1_000).toFixed(1) + "K";
    return num.toLocaleString();
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const getRankColor = (score) => {
    if (score >= 80) return "#28A745";
    if (score >= 60) return "#FFC107";
    if (score >= 40) return "#FF8C00";
    return "#DC3545";
  };

  const getRankLabel = (score) => {
    if (score >= 80) return "Excellent";
    if (score >= 60) return "Good";
    if (score >= 40) return "Average";
    return "Low";
  };

  // ── Metric Card sub-component ────────────────────────────────────
  const MetricCard = ({ icon, label, value, color }) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: "14px 16px",
        backgroundColor: "var(--background-light)",
        borderRadius: 10,
        flex: "1 1 calc(50% - 8px)",
        minWidth: 160,
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          backgroundColor: `${color || "var(--primary-color)"}15`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <FontAwesomeIcon
          icon={icon}
          style={{ color: color || "var(--primary-color)", fontSize: "0.95rem" }}
        />
      </div>
      <div>
        <div
          style={{
            fontSize: "0.78rem",
            color: "var(--text-gray)",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: 0.4,
          }}
        >
          {label}
        </div>
        <div
          style={{
            fontSize: "1.2rem",
            fontWeight: 700,
            color: "var(--text-dark)",
            marginTop: 2,
          }}
        >
          {value}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <SideBar />
      <div className="dashboard-container">
        {/* ── Header with Back Button ──────────────────────────── */}
        <div className="dashboard-header">
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <button
              className="btn btn-outline btn-sm"
              onClick={() => navigate("/influencer-leaderboard")}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                borderRadius: 8,
              }}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              Leaderboard
            </button>
            <div>
              <h1 className="dashboard-title" style={{ marginBottom: 2 }}>
                <FontAwesomeIcon
                  icon={faUser}
                  style={{ marginRight: 10, color: "var(--primary-color)" }}
                />
                Influencer Profile
              </h1>
              <p className="dashboard-subtitle" style={{ margin: 0 }}>
                Detailed profile with metrics and engagement data.
              </p>
            </div>
          </div>
        </div>

        {/* ── Error ────────────────────────────────────────────── */}
        {error && (
          <div className="alert alert-error">
            {error}
            <button
              className="btn btn-primary btn-sm"
              onClick={() => navigate("/influencer-leaderboard")}
              style={{ marginLeft: 16 }}
            >
              <FontAwesomeIcon icon={faTrophy} style={{ marginRight: 6 }} />
              Go to Leaderboard
            </button>
          </div>
        )}

        {/* ── Loading ──────────────────────────────────────────── */}
        {loading && (
          <div style={{ textAlign: "center", padding: 40 }}>
            <div className="spinner" />
            <p style={{ color: "var(--text-gray)", marginTop: 8 }}>
              Fetching profile...
            </p>
          </div>
        )}

        {/* ── Profile Card ─────────────────────────────────────── */}
        {profile && !loading && (
          <div className="fade-in">
            {/* Cover + Avatar Section */}
            <div
              className="card"
              style={{ padding: 0, overflow: "hidden", marginBottom: 20 }}
            >
              {/* Cover Image */}
              <div
                style={{
                  height: 160,
                  background: profile.cover_picture
                    ? `url(${profile.cover_picture}) center/cover no-repeat`
                    : "linear-gradient(135deg, var(--primary-color), var(--primary-light))",
                  position: "relative",
                }}
              >
                {/* Ranking Badge */}
                <div
                  style={{
                    position: "absolute",
                    top: 14,
                    right: 14,
                    backgroundColor: getRankColor(profile.ranking_score),
                    color: "#fff",
                    padding: "6px 14px",
                    borderRadius: 20,
                    fontSize: "0.8rem",
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                  }}
                >
                  <FontAwesomeIcon icon={faStar} />
                  {profile.ranking_score?.toFixed(1)} · {getRankLabel(profile.ranking_score)}
                </div>
              </div>

              {/* Profile Info */}
              <div style={{ padding: "0 24px 24px", position: "relative" }}>
                {/* Avatar */}
                <div
                  style={{
                    width: 88,
                    height: 88,
                    borderRadius: "50%",
                    border: "4px solid #fff",
                    overflow: "hidden",
                    marginTop: -44,
                    backgroundColor: "var(--background-light)",
                    boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
                  }}
                >
                  {profile.profile_picture ? (
                    <img
                      src={profile.profile_picture.replace("_normal", "_400x400")}
                      alt={profile.name}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faUser}
                        style={{ fontSize: "2rem", color: "var(--text-gray)" }}
                      />
                    </div>
                  )}
                </div>

                {/* Name + Username */}
                <div style={{ marginTop: 12 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      flexWrap: "wrap",
                    }}
                  >
                    <h2
                      style={{
                        fontSize: "1.4rem",
                        margin: 0,
                        color: "var(--text-dark)",
                      }}
                    >
                      {profile.name || profile.username}
                    </h2>
                    {profile.is_blue_verified && (
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        style={{ color: "#1DA1F2", fontSize: "1.1rem" }}
                        title="Verified"
                      />
                    )}
                    {profile.category && (
                      <span
                        style={{
                          backgroundColor: "var(--primary-color)",
                          color: "#fff",
                          padding: "3px 10px",
                          borderRadius: 12,
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          textTransform: "capitalize",
                        }}
                      >
                        {profile.category}
                      </span>
                    )}
                  </div>

                  <p
                    style={{
                      color: "var(--text-gray)",
                      fontSize: "0.95rem",
                      margin: "4px 0 0",
                    }}
                  >
                    @{profile.username}
                  </p>
                </div>

                {/* Description */}
                {profile.description && (
                  <p
                    style={{
                      marginTop: 12,
                      color: "var(--text-light)",
                      fontSize: "0.92rem",
                      lineHeight: 1.6,
                      whiteSpace: "pre-line",
                    }}
                  >
                    {profile.description}
                  </p>
                )}

                {/* Meta info row */}
                <div
                  style={{
                    display: "flex",
                    gap: 20,
                    flexWrap: "wrap",
                    marginTop: 14,
                    fontSize: "0.85rem",
                    color: "var(--text-gray)",
                  }}
                >
                  {profile.location && (
                    <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <FontAwesomeIcon icon={faMapMarkerAlt} />
                      {profile.location}
                    </span>
                  )}
                  {profile.created_at && (
                    <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <FontAwesomeIcon icon={faCalendarAlt} />
                      Joined {formatDate(profile.created_at)}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* ── Metrics Grid ─────────────────────────────────── */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 14,
                marginBottom: 20,
              }}
            >
              <MetricCard
                icon={faUsers}
                label="Followers"
                value={formatNumber(profile.metrics?.followers ?? profile.followers)}
                color="#1DA1F2"
              />
              <MetricCard
                icon={faUser}
                label="Following"
                value={formatNumber(profile.metrics?.following ?? profile.following)}
                color="#6C63FF"
              />
              <MetricCard
                icon={faNewspaper}
                label="Tweets"
                value={formatNumber(
                  profile.metrics?.tweets_count ?? profile.tweets_count
                )}
                color="var(--primary-color)"
              />
              <MetricCard
                icon={faHeart}
                label="Favourites"
                value={formatNumber(profile.metrics?.favourites_count)}
                color="#E91E63"
              />
              <MetricCard
                icon={faCamera}
                label="Media"
                value={formatNumber(profile.metrics?.media_count)}
                color="#FF9800"
              />
              {/* <MetricCard
                icon={faChartLine}
                label="Ranking Score"
                value={
                  profile.ranking_score != null
                    ? profile.ranking_score.toFixed(1)
                    : "—"
                }
                color={getRankColor(profile.ranking_score)}
              /> */}
            </div>

            {/* ── CTA Cards ──────────────────────────────────── */}
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {/* View Tweets */}
              <div
                className="card"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  cursor: "pointer",
                  borderLeft: "4px solid var(--primary-color)",
                }}
                onClick={() =>
                  navigate(`/influencer-tweets?username=${profile.username}`)
                }
              >
                <div>
                  <h3
                    style={{
                      margin: 0,
                      fontSize: "1.05rem",
                      color: "var(--text-dark)",
                    }}
                  >
                    View Tweets & Classification
                  </h3>
                  <p
                    style={{
                      margin: "4px 0 0",
                      color: "var(--text-gray)",
                      fontSize: "0.88rem",
                    }}
                  >
                    See GPT-classified tweets with engagement metrics and summaries
                  </p>
                </div>
                <FontAwesomeIcon
                  icon={faArrowRight}
                  style={{ color: "var(--primary-color)", fontSize: "1.2rem" }}
                />
              </div>

              {/* Analytics Dashboard */}
              <div
                className="card"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  cursor: "pointer",
                  borderLeft: "4px solid #1DA1F2",
                }}
                onClick={() =>
                  navigate(`/influencer-analytics?username=${profile.username}`)
                }
              >
                <div>
                  <h3
                    style={{
                      margin: 0,
                      fontSize: "1.05rem",
                      color: "var(--text-dark)",
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faChartLine}
                      style={{ marginRight: 8, color: "#1DA1F2" }}
                    />
                    Analytics Dashboard
                  </h3>
                  <p
                    style={{
                      margin: "4px 0 0",
                      color: "var(--text-gray)",
                      fontSize: "0.88rem",
                    }}
                  >
                    Full engagement analytics, classification breakdown, posting behavior & trends
                  </p>
                </div>
                <FontAwesomeIcon
                  icon={faArrowRight}
                  style={{ color: "#1DA1F2", fontSize: "1.2rem" }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default InfluencerProfile;